"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

type Item = { question: string; answer: string };

export default function FAQ({
  eyebrow,
  title,
  items,
}: {
  eyebrow: string;
  title: string;
  items: Item[];
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="border-y border-dashed border-white/15 bg-[#0b0b0d] py-24 md:py-32">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl">
          <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-body">
            {eyebrow}
          </p>
          <h2 className="mt-6 font-heading text-4xl md:text-5xl leading-[1.1] font-light text-white">
            {title}
          </h2>
        </div>

        {/* Accordion */}
        <div className="mt-14 divide-y divide-white/10 border-y border-white/10">
          {items.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div key={i}>
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="group flex w-full items-start justify-between gap-8 py-6 text-left transition hover:bg-white/[0.015]"
                >
                  <span className="font-heading text-lg md:text-2xl font-light text-white/90 group-hover:text-white transition">
                    {item.question}
                  </span>
                  <span
                    className={[
                      "mt-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/20 text-white/70 transition-all duration-300",
                      isOpen
                        ? "rotate-45 bg-white/10 text-white"
                        : "rotate-0 group-hover:bg-white/5",
                    ].join(" ")}
                    aria-hidden
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="h-3.5 w-3.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    >
                      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                    </svg>
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="pb-7 pr-12 text-base leading-relaxed text-white/60 font-body font-light">
                        {item.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
